import {
  useContext,
  useCallback,
  useMemo,
} from 'react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';

import FirebaseContext from '../context/FirebaseContext';
import { DateFormat } from '../utils/date';
import {
  prepareUploadBlob,
  storageURLtoBlob,
  storageURLtoBlobURL,
  storageJSONURLtoJSONData,
} from '../utils/dataProcessing';

const StorageProcessState = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  INITIAL: 'INITIAL',
};

const useStorage = () => {
  const {
    firebase: {
      storage,
    },
  } = useContext(FirebaseContext);

  /**
   * Listens and handles state changes for a Firebase upload task.
   * @param {firebase.storage.UploadTask} uploadTask Firebase storage upload task
   * @param {String} dataUrl Url representation of the file to upload
   * @param {String} storageRef Firebase storage reference of the file to upload
   * @param {String} errorMessage Sentry extra error message
   * @param {Function} [onProgress] Called with the uploaded percentage (0-100) as bytes land
   * @param {Boolean} [resolveFileDetails=true] Whether to fetch the download URL and metadata on success. Off for
   * uploads the uploader may write but not read back.
   * @return {Promise<Object>} Promise that resolves to an object containing the storage task state and, if successful,
   * the file's storage reference
   */
  const attachListenerToStorageTask = useCallback(({
    uploadTask,
    dataUrl,
    storageRef,
    errorMessage,
    fileId = '',
    onProgress = null,
    resolveFileDetails = true,
  }) => new Promise((resolve) => {
    uploadTask.on(
      'state_changed',
      onProgress && ((snapshot) => {
        onProgress(snapshot.totalBytes ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100 : 0);
      }),
      (error) => {
        Sentry.captureException(error, {
          extra: {
            message: errorMessage,
            fileRef: storageRef,
          },
        });
        URL.revokeObjectURL(dataUrl);
        resolve({
          state: StorageProcessState.FAILED,
          fileId,
        });
      },
      async () => {
        if (!resolveFileDetails) {
          resolve({
            state: StorageProcessState.SUCCESS,
            fileRef: storageRef,
            fileId,
          });
          return;
        }
        const publicUrl = await uploadTask.snapshot.ref.getDownloadURL();
        const metadata = await uploadTask.snapshot.ref.getMetadata();
        URL.revokeObjectURL(dataUrl);
        resolve({
          state: StorageProcessState.SUCCESS,
          fileRef: storageRef,
          publicUrl,
          metadata,
          fileId,
        });
      },
    );
  }), []);

  /**
   * Uploads a JSON string to Firebase storage
   * @param {String} jsonString The JSON data as a string
   * @param {String} storagePath Firebase storage reference path to upload the file
   * @param {String} fileName Filename without extension
   * @return {Promise<Object>} Promise that resolves to an object containing the storage task state and, if successful,
   * the file's storage reference
   */
  const uploadJSON = useCallback(async (
    jsonString,
    storagePath,
    fileName,
    useUniqueName = false,
  ) => {
    const rootRef = storage.ref();
    const storageFileName = useUniqueName ? `${fileName}-${uuidv4()}` : fileName;

    const blob = new Blob([jsonString], { type: 'application/json' });
    const storageRef = `${storagePath}/${storageFileName}.json`;

    const uploadTask = rootRef.child(storageRef).put(blob);

    return attachListenerToStorageTask({
      uploadTask,
      dataUrl: null, // No data URL here since it's just a JSON string, not an image or other binary data.
      storageRef,
    });
  }, [
    storage,
    attachListenerToStorageTask,
  ]);

  /**
   * Uploads a file to Firebase storage
   * @param {String} dataUrl Url representation of the file to upload
   * @param {String} fileFormat Format of the file to upload
   * @param {String} storageRef Firebase storage reference of the file to upload
   * @param {String} fileName optional filename
   * @param {String} fileType optional file mime type
   * @param {Boolean} [useExactFileName=false] optional flag to use the exact file name
   * @param {Function} [onProgress] Called with the uploaded percentage (0-100)
   * @return {Promise<Object>} Promise that resolves to an object containing the storage task state and, if successful,
   * the file's storage reference
   */
  const uploadAttachment = useCallback(async (
    dataUrl,
    fileFormat,
    storagePath,
    fileName = '',
    fileType = '',
    fileId = '',
    useExactFileName = false,
    onProgress = null,
  ) => {
    const rootRef = storage.ref();
    // Prepared first: a HEIC upload is stored as JPEG, so the extension and the
    // content type both come from the bytes rather than the original file name.
    const {
      blob: fileDataBlob,
      fileFormat: storedFormat,
      fileType: storedType,
    } = await prepareUploadBlob({ url: dataUrl, fileFormat, fileType });

    const storageFileName = fileName
      ? `${fileName}${useExactFileName ? '' : `-${uuidv4()}`}.${storedFormat}`
      : `${moment().format(DateFormat.DEFAULT_DATE_WITH_TIME_FORMAT)}-${uuidv4()}.${storedFormat}`;
    const storageRef = `${storagePath}/${storageFileName}`;

    const uploadTask = rootRef.child(storageRef).put(fileDataBlob, { contentType: storedType || storedFormat });

    return attachListenerToStorageTask({
      uploadTask,
      dataUrl,
      storageRef,
      fileId,
      onProgress,
    });
  }, [
    storage,
    attachListenerToStorageTask,
  ]);

  /**
   * Uploads a File or Blob as-is to an exact storage path — no renaming, no conversion.
   * @param {Object} params
   * @param {Blob} params.file The file to upload
   * @param {String} params.storagePath Full storage path, including the file name
   * @param {Object} [params.metadata] Upload metadata (contentType, customMetadata)
   * @param {Function} [params.onProgress] Called with the uploaded percentage (0-100)
   * @return {Promise<Object>} Promise that resolves to an object containing the storage task state and, if
   * successful, the file's storage reference
   */
  const uploadFile = useCallback(({
    file,
    storagePath,
    metadata,
    onProgress,
  }) => {
    const uploadTask = storage.ref().child(storagePath).put(file, metadata);

    return attachListenerToStorageTask({
      uploadTask,
      dataUrl: null,
      storageRef: storagePath,
      errorMessage: 'Error occurred while uploading a file to storage',
      onProgress,
      resolveFileDetails: false,
    });
  }, [
    storage,
    attachListenerToStorageTask,
  ]);

  /**
   * Uploads multiple files to Firebase storage
   * @param {Object[]} filesToAdd Array of objects containing files info
   * @param {String} storagePath Firebase storage path were the files will be uploaded
   * @return {Promise<Object[]>} Promise that resolves into an array of objects with the upload results for all files
   */
  const uploadAttachments = useCallback((
    filesToAdd,
    storagePath,
  ) => Promise.all(filesToAdd.map((fileDetail) => {
    const {
      dataUrl,
      format: fileFormat,
      fileName,
      fileType,
      fileId = '',
    } = fileDetail;

    return uploadAttachment(
      dataUrl,
      fileFormat,
      storagePath,
      fileName,
      fileType,
      fileId,
    );
  })), [
    uploadAttachment,
  ]);

  /**
   * Deletes a file from Firebase storage
   * @param {String} storagePath Firebase storage path of the file to delete
   * @param {Object} [options]
   * @param {Boolean} [options.ignoreNotFound] Treat a missing object as success, for callers whose
   * path may point at bytes that never landed.
   * @return {{state: String, fileRef: String}} Object that contains the result of the operation and it's file path
   */
  const deleteAttachment = useCallback(async (storagePath, { ignoreNotFound = false } = {}) => {
    try {
      await storage
        .ref()
        .child(storagePath)
        .delete();
      return {
        state: StorageProcessState.SUCCESS,
        fileRef: storagePath,
      };
    } catch (error) {
      if (ignoreNotFound && error.code === 'storage/object-not-found') {
        return {
          state: StorageProcessState.SUCCESS,
          fileRef: storagePath,
        };
      }
      Sentry.captureException(error, {
        extra: {
          message: 'Error occurred while deleting a file from storage',
          fileRef: storagePath,
        },
      });
      return {
        state: StorageProcessState.FAILED,
        fileRef: storagePath,
      };
    }
  }, [
    storage,
  ]);

  /**
   * Deletes multiple files from Firebase storage
   * @param {Object[]} filesToDelete Array of objects containing the Firebase storage path of the
   * files to delete
   * @return {Promise<Object[]>} Promise that resolves into an array of objects with the delete results for all files
   */
  const deleteAttachments = useCallback((filesToDelete) => Promise.all(filesToDelete.map((fileDetail) => {
    const { storagePath } = fileDetail;

    return deleteAttachment(storagePath);
  })), [
    deleteAttachment,
  ]);

  /**
   * Downloads a file from Firebase storage and returns its blob
   * @param {String} storagePath Firebase storage path of the file to download
   * @return {Promise<Blob>} Promise that resolves into the file's blob
   */
  const getBlob = useCallback(async (storagePath) => storageURLtoBlob(storagePath, storage), [
    storage,
  ]);

  /**
   * Downloads a file from Firebase storage and creates an object URL using createObjectURL
   * @param {String} storagePath Firebase storage path of the file to download
   * @return {Promise<String>} Promise that resolves into an object URL string
   */
  const getBlobUrl = useCallback(async (storagePath) => storageURLtoBlobURL(storagePath, storage), [
    storage,
  ]);

  /**
   * Downloads multiple files from Firebase storage and creates Object URLs for every file
   * @param {String[]} storagePaths Array of strings containing the Firebase storage paths
   * @return {Promise<String[]>} Promise that resolves into an array of objects URL strings
   */
  const getBlobUrls = useCallback((storagePaths) => {
    const promises = storagePaths.map(async (storagePath) => {
      const { fileRef, fileId } = storagePath;
      return {
        storagePath: fileRef,
        dataUrl: await getBlobUrl(storagePath.fileRef),
        name: (fileRef.split('/').pop()).split('-')[0],
        type: fileRef.split('.').pop(),
        fileId: fileId || '',
      };
    });
    return Promise.all(promises);
  }, [
    getBlobUrl,
  ]);

  /**
   * Downloads a JSON file from Firebase storage and creates a JSON object
   * @param {String} storagePath Firebase storage path of the file to download
   * @return {Promise<Object>} Promise that resolves into the JSON data
   */
  const getJSONData = useCallback(async (storagePath) => storageJSONURLtoJSONData(storagePath, storage), [
    storage,
  ]);

  /**
   * Creates a File object from a data URL
   * @param {String} dataUrl Data URL of the file
   * @param {String} fileName Name of the file
   * @param {String} fileType MIME type of the file
   * @return {Promise<File>} Promise that resolves into a File object
   */
  const createFileFromDataUrl = useCallback(async (dataUrl, fileName, fileType, fileId) => {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return {
      file: new File([blob], fileName, { type: fileType }),
      fileId,
    };
  }, []);

  /**
   * Creates multiple File objects from data URLs
   * @param {Object[]} fileDetails Array of objects containing the data URL, name and MIME type of the files
   * @return {Promise<File[]>} Promise that resolves into an array of File objects
   */
  const createFilesFromDataUrls = useCallback(async (fileDetails) => {
    const filePromises = fileDetails.map(async (fileDetail) => {
      const {
        dataUrl, name, type, fileId,
      } = fileDetail;
      return createFileFromDataUrl(dataUrl, name, type, fileId);
    });
    return Promise.all(filePromises);
  }, [createFileFromDataUrl]);

  return useMemo(() => ({
    uploadJSON,
    uploadAttachment,
    uploadAttachments,
    uploadFile,
    deleteAttachment,
    deleteAttachments,
    getBlob,
    getBlobUrl,
    getBlobUrls,
    getJSONData,
    createFileFromDataUrl,
    createFilesFromDataUrls,
  }), [
    uploadJSON,
    uploadAttachment,
    uploadAttachments,
    uploadFile,
    deleteAttachment,
    deleteAttachments,
    getBlob,
    getBlobUrl,
    getBlobUrls,
    getJSONData,
    createFileFromDataUrl,
    createFilesFromDataUrls,
  ]);
};

export default useStorage;
export {
  StorageProcessState,
};
